// PARTS-SERVING-1 (2026-10-06): the PINNED diagram hotspot popup.
//
// The hover popup (DiagramInfoCardV2) is pointer-events:none and follows the
// cursor, so its sheet links can never be clicked and a long part list can
// never be scrolled. When the user CLICKS a hotspot that carries parts /
// details / links (the click that already selects + highlights it,
// OVERLAY-CLICK-HIGHLIGHT-1), the viewer mounts this card instead: same card,
// same chrome, but it stays at the click point, is clamped inside the
// diagram viewport, scrolls internally, and its links are buttons.
//
// Size per 3-tier breakpoint (useBreakpoint — never MUI useMediaQuery); the
// height is also capped to the viewport the card lives in.
//
// Lifetime is owned by the viewer: it shows while that hotspot stays the
// selected one (re-click / backdrop click / another selection / page change
// close it). It is rendered OUTSIDE the pan/zoom viewport element, so a
// click, drag or wheel on the card never pans, zooms or clears the selection.

import React from "react";
import { DiagramHotspot } from "../../../../shared/chatBot/models/chatBotModels";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import DiagramInfoCardV2, { InfoCardBounds } from "./DiagramInfoCardV2";

/** Max card height (px) per tier, before the viewport-height cap. */
export const PINNED_CARD_MAX_HEIGHT = {
  phone: 280,
  tablet: 360,
  desktop: 420,
} as const;

interface Props {
  hotspot: DiagramHotspot;
  /** Click point (client coordinates). */
  x: number;
  y: number;
  /** The diagram viewport the card is clamped into. */
  viewportEl: HTMLElement | null;
  /** Opens a linked sheet; absent = links stay plain text. */
  onOpenLink?: (pageId: number) => void;
}

const DiagramPinnedInfoCardV2: React.FC<Props> = ({
  hotspot,
  x,
  y,
  viewportEl,
  onOpenLink,
}) => {
  const { breakpoint } = useBreakpoint();
  const rect = viewportEl ? viewportEl.getBoundingClientRect() : null;
  const bounds: InfoCardBounds | undefined =
    rect && rect.width > 0 && rect.height > 0
      ? { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom }
      : undefined;
  const areaHeight = bounds ? bounds.bottom - bounds.top : window.innerHeight;
  const maxHeight = Math.max(
    120,
    Math.min(PINNED_CARD_MAX_HEIGHT[breakpoint], areaHeight - 8)
  );

  return (
    <DiagramInfoCardV2
      hotspot={hotspot}
      mouseX={x}
      mouseY={y}
      interactive
      bounds={bounds}
      maxHeight={maxHeight}
      onOpenLink={onOpenLink}
    />
  );
};

export default DiagramPinnedInfoCardV2;
